// Convert any string to a URL-friendly slug
// "Food and Health" → "food-and-health"
// "Best Foods for You!" → "best-foods-for-you"
export function toSlug(str = '') {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

// Build a combined title+id slug: "best-foods-for-you--5466b00a-39cf-46fc-81e1-9972429ce757"
// Double dash (--) separates title slug from ID so extraction is always reliable.
// The title portion is capped so URLs stay a sane length for SEO and so the
// on-disk path during prerender never exceeds the OS path limit (Windows ~260).
// The ID after "--" is what identifies the item, so truncating the readable
// prefix is safe and does not affect extractIdFromSlug.
const MAX_TITLE_SLUG_LEN = 60;
export function buildSlug(title, id) {
  const titleSlug = toSlug(title).slice(0, MAX_TITLE_SLUG_LEN).replace(/-+$/, '');
  return `${titleSlug}--${id}`;
}

// Extract the original ID from a combined slug
// "best-foods-for-you--5466b00a-39cf-46fc-81e1-9972429ce757" → "5466b00a-39cf-46fc-81e1-9972429ce757"
//
// The ID always comes after the FINAL "--". We use lastIndexOf (not indexOf)
// so this stays correct even when the title portion of the slug itself
// contains "--" — which happens for titles with " - ", " | " or ":" and is
// exactly what the backend-generated sitemap URLs contain. With the old
// indexOf, those URLs extracted the wrong ID and every such page 404'd when
// Google crawled it. (An ID never contains "--", so the last "--" is always
// the real separator.)
export function extractIdFromSlug(slug = '') {
  const separatorIndex = slug.lastIndexOf('--');
  if (separatorIndex !== -1) return slug.slice(separatorIndex + 2);
  return slug;
}

// Blog category slug ↔ display name mapping
const BLOG_SLUG_TO_CATEGORY = {
  'fashion-and-beauty': 'Fashion and Beauty',
  'electronics': 'Electronics',
  'mobile': 'Mobile',
  'home': 'Home',
  'food-and-health': 'Food and Health',
  'others': 'Others',
};

// Offer type slug ↔ display name mapping
const OFFER_SLUG_TO_TYPE = {
  'shopping': 'Shopping',
  'electronics': 'Electronics',
  'fashion': 'Fashion',
  'food': 'Food',
  'card-offer': 'Card Offer',
  'travel': 'Travel',
  'others': 'Others',
};

export function blogCategoryFromSlug(slug = '') {
  return BLOG_SLUG_TO_CATEGORY[slug] || slug;
}

export function offerTypeFromSlug(slug = '') {
  return OFFER_SLUG_TO_TYPE[slug] || slug;
}

// Whether a slug names a real offer category, so /offers/<slug> can tell a
// category landing page apart from a 404. Mirrors isKnownStoreSlug below.
export function isKnownOfferTypeSlug(slug = '') {
  return Object.prototype.hasOwnProperty.call(OFFER_SLUG_TO_TYPE, slug.toLowerCase());
}

// Store slug ↔ display-name mapping for store-landing pages
const STORE_SLUG_TO_NAME = {
  'flipkart': 'Flipkart',
  'amazon': 'Amazon',
  'myntra': 'Myntra',
  'ajio': 'Ajio',
  'others': 'Others',
};

export const KNOWN_STORES = ['Flipkart', 'Amazon', 'Myntra', 'Ajio'];

export function storeFromSlug(slug = '') {
  return STORE_SLUG_TO_NAME[slug.toLowerCase()] || slug;
}

export function isKnownStoreSlug(slug = '') {
  return Object.prototype.hasOwnProperty.call(STORE_SLUG_TO_NAME, slug.toLowerCase());
}
